import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';
import { ensureMobileDrawerLoaded } from '@theme/header-lazy-menus';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** Invalidates in-flight close cleanups when the drawer reopens. */
  #closeGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * Stop the browser from toggling <details open> natively. We drive open
   * state via menu-open + optional open attr; Safari re-toggles after our
   * handler and breaks the second open/close cycle.
   * @param {Event | undefined} event
   */
  #preventNativeDetailsToggle(event) {
    if (event?.cancelable) {
      event.preventDefault();
    }
  }

  /**
   * @param {boolean} locked
   */
  #setScrollLock(locked) {
    if (locked) {
      document.documentElement.setAttribute('scroll-lock', '');
    } else {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  /**
   * Get the trigger <details> element associated with the event target.
   *
   * Mobile mega-menu panels are emitted as siblings of `.menu-drawer__navigation`
   * inside `.menu-drawer` (see snippets/header-drawer.liquid). Each panel
   * carries `data-trigger-id` pointing back to the trigger <details>. So if
   * the click happened inside a panel, resolve via that attribute. Otherwise
   * fall back to the closest ancestor <details>, then to the outer drawer.
   *
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    if (!event.target.isConnected) return this.refs.details;

    const siblingPanel = event.target.closest('.menu-drawer__submenu');
    if (siblingPanel instanceof HTMLElement && siblingPanel.dataset.triggerId) {
      const trigger = document.getElementById(siblingPanel.dataset.triggerId);
      if (trigger instanceof HTMLDetailsElement) return trigger;
    }

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  async toggle(event) {
    if (this.classList.contains('header-drawer--lazy-shell')) {
      this.#preventNativeDetailsToggle(event);
      await ensureMobileDrawerLoaded();
      const loadedDrawer = await this.#getLoadedMobileDrawer();
      if (loadedDrawer) {
        return loadedDrawer.open();
      }
      return;
    }

    this.#preventNativeDetailsToggle(event);

    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  async open(event) {
    if (this.classList.contains('header-drawer--lazy-shell')) {
      this.#preventNativeDetailsToggle(event);
      await ensureMobileDrawerLoaded();
      const loadedDrawer = await this.#getLoadedMobileDrawer();
      if (loadedDrawer) {
        loadedDrawer.open();
      }
      return;
    }

    this.#preventNativeDetailsToggle(event);

    const details = this.#getDetailsElement(event);
    const isMainDrawer = details === this.refs.details;
    if (isMainDrawer) {
      this.#closeGeneration += 1;
    }

    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (isMainDrawer) {
      details.setAttribute('open', '');
      this.#setScrollLock(true);
    }

    // Look up the panel via aria-controls (set in snippets/header-drawer.liquid
    // for flat-mode panels rendered as direct children of .menu-drawer).
    // Falls back to a descendant submenu for accordion mode and the outer
    // drawer itself.
    const panel = this.#getControlledPanel(summary);

    // When opening a flat-mode panel that is a sibling of .menu-drawer__navigation,
    // .menu-drawer has `transform` (making it the containing block for the
    // panel's position:fixed) AND `overflow-y: auto`. If the user has
    // scrolled the main menu before tapping a top-level item, the panel's
    // top would appear offset by the scroll position. Resetting scroll here
    // before the slide animation starts keeps the panel pinned to the top.
    if (panel) {
      const mainDrawer = this.querySelector('.menu-drawer');
      if (mainDrawer && mainDrawer.scrollTop !== 0) mainDrawer.scrollTop = 0;
      syncMegaMenuCustomContent(
        panel.querySelector('.menu-drawer__mega-custom-content')
      );
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      if (panel) panel.classList.add('menu-drawer__submenu--open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer =
        panel ||
        details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(panel || details), {
        subtree: false,
      });
    });
  }

  /**
   * Resolve the panel a summary controls. Triggers in the main mobile nav
   * have `aria-controls="<panel-id>"` pointing at the matching
   * `.menu-drawer__submenu` panel rendered as a sibling of
   * `.menu-drawer__navigation`.
   * @param {Element} summary
   * @returns {HTMLElement | null}
   */
  #getControlledPanel(summary) {
    const id = summary.getAttribute('aria-controls');
    if (!id) return null;
    return /** @type {HTMLElement | null} */ (
      this.querySelector(`#${CSS.escape(id)}`)
    );
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#preventNativeDetailsToggle(event);
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const isMainDrawer = details === this.refs.details;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (isMainDrawer) {
      details.removeAttribute('open');
      this.#setScrollLock(false);
    }

    // Look up a hoisted panel; fall back to a nested submenu/.menu-drawer.
    const panel = this.#getControlledPanel(summary);
    if (panel) panel.classList.remove('menu-drawer__submenu--open');

    const closeGeneration = isMainDrawer ? ++this.#closeGeneration : this.#closeGeneration;

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer =
      panel || details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (isMainDrawer && closeGeneration !== this.#closeGeneration) return;
        if (details.classList.contains('menu-open')) return;

        resetTrigger(details);
        if (isMainDrawer) {
          removeTrapFocus();
          // Reset all open triggers and their hoisted panels
          const openDetails = this.querySelectorAll(
            'details[open]:not(accordion-custom > details)'
          );
          openDetails.forEach(resetTrigger);
          this.querySelectorAll(
            '.menu-drawer__submenu--open'
          ).forEach((p) => p.classList.remove('menu-drawer__submenu--open'));
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  /**
   * @returns {Promise<HeaderDrawer | null>}
   */
  async #getLoadedMobileDrawer() {
    await customElements.whenDefined('header-drawer');
    const loadedDrawer = document.querySelector(
      '[data-header-part="mobile-drawer"] header-drawer:not(.header-drawer--lazy-shell)'
    );
    return loadedDrawer instanceof HeaderDrawer ? loadedDrawer : null;
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open trigger <details> element to its original state.
 * Renamed from `reset` for clarity now that close logic also handles
 * hoisted panels. Behaviour for the trigger itself is unchanged.
 *
 * @param {HTMLDetailsElement} element
 */
function resetTrigger(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/**
 * Show the mega-menu group block that matches this panel's active link.
 * @param {Element | null | undefined} container
 */
function syncMegaMenuCustomContent(container) {
  if (!(container instanceof HTMLElement)) return;

  const activeUrlKey = container.getAttribute('data-active-url-key') || '';
  const activeNameKey = container.getAttribute('data-active-link-key') || '';

  container.querySelectorAll('.mega-menu-block').forEach((block) => {
    const blockUrlKey = block.getAttribute('data-menu-url-key') || '';
    const blockNameKey = block.getAttribute('data-menu-link-key') || '';
    const urlMatch =
      activeUrlKey !== '' && blockUrlKey !== '' && blockUrlKey === activeUrlKey;
    const nameMatch =
      activeNameKey !== '' && blockNameKey !== '' && blockNameKey === activeNameKey;
    block.classList.toggle('mega-menu-block--active', urlMatch || nameMatch);
  });
}

